import React, { useEffect, useState } from "react";
import { PhoneCall, PhoneIncoming, PhoneOutgoing, MessageSquare, Loader2, Sparkles, Clock, X, CheckCircle2, AlertCircle, PhoneForwarded } from "lucide-react";
import { twilio } from "@/lib/api";
import { toast } from "sonner";

function fmt(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleString(); } catch (_) { return ts; }
}

// Ordered list of associates who handled the call (Linda -> Jim), from the transcript.
function handoffChain(transcript) {
  const names = [];
  (transcript || []).forEach((t) => {
    if (t.role && !["caller", "contact", "system"].includes(t.role) && !names.includes(t.role)) names.push(t.role);
  });
  return names;
}

export default function CallLog() {
  const [tab, setTab] = useState("calls");
  const [data, setData] = useState({ calls: [], sms: [] });
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const loadScheduled = () => twilio.scheduled().then((d) => setScheduled(d.items || [])).catch(() => setScheduled([]));

  useEffect(() => {
    Promise.all([
      twilio.logs().then((d) => setData(d)).catch(() => setData({ calls: [], sms: [] })),
      loadScheduled(),
    ]).finally(() => setLoading(false));
  }, []);

  const cancelScheduled = async (id) => {
    try {
      const r = await twilio.cancelScheduled(id);
      if (r.ok) { toast.success("Reminder canceled"); loadScheduled(); }
      else { toast.error("Couldn't cancel", { description: r.error || "" }); }
    } catch (e) { toast.error("Couldn't cancel", { description: e?.response?.data?.detail || "" }); }
  };

  const pendingCount = scheduled.filter((s) => s.status === "pending").length;

  return (
    <div data-testid="call-log-page">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 font-display text-3xl font-semibold text-white sm:text-4xl">
          <PhoneCall className="text-[#00E5FF]" size={28} /> Call Log
        </h1>
        <p className="mt-1 text-sm text-slate-400">Review phone calls and text conversations your associates handled — with transcripts and AI summaries.</p>
      </div>

      <div className="mb-4 flex gap-2">
        {[["calls", "Phone calls", data.calls.length], ["sms", "Text messages", data.sms.length], ["scheduled", "Scheduled", pendingCount]].map(([id, label, n]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${tab === id ? "bg-[#00E5FF] text-[#030A16]" : "border border-white/10 text-slate-300 hover:text-white"}`}
            data-testid={`calllog-tab-${id}`}>
            {label} <span className="font-mono">{n}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-slate-500"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : tab === "calls" ? (
        <div className="space-y-2" data-testid="calllog-calls">
          {data.calls.length === 0 ? (
            <div className="rounded-xl border border-white/8 bg-[#030A16]/40 p-6 text-center text-sm text-slate-500">No calls logged yet. Calls appear here after Linda answers or a call is placed.</div>
          ) : data.calls.map((c) => {
            const chain = handoffChain(c.transcript);
            return (
            <div key={c.id} className="rounded-xl border border-white/8 bg-[#030A16]/40 p-4" data-testid={`call-row-${c.id}`}>
              <div className="flex items-start gap-3">
                <span className="mt-0.5 grid h-9 w-9 place-items-center rounded-lg bg-white/5 text-[#00E5FF]">
                  {c.direction === "outbound" ? <PhoneOutgoing size={16} /> : <PhoneIncoming size={16} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">{c.direction === "outbound" ? (c.to_name || c.to || "Outbound call") : (c.from || "Inbound call")}</span>
                    <span className="rounded-full border border-white/10 px-1.5 py-0.5 text-[10px] font-mono uppercase text-slate-400">{c.direction}</span>
                  </div>
                  <div className="text-[11px] text-slate-500">{fmt(c.started_at)}</div>
                  {chain.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1 text-[11px]" data-testid={`call-routing-${c.id}`}>
                      <span className="text-slate-500">Handled by</span>
                      {chain.map((n, i) => (
                        <React.Fragment key={n}>
                          {i > 0 && <PhoneForwarded size={10} className="text-[#8A2BE2]" />}
                          <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-slate-200">{n}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  {c.summary && (
                    <div className="mt-2 flex items-start gap-1.5 text-xs text-slate-300"><Sparkles size={12} className="mt-0.5 shrink-0 text-[#8A2BE2]" /><span className="whitespace-pre-wrap">{c.summary}</span></div>
                  )}
                  <button onClick={() => setOpen(open === c.id ? null : c.id)} className="mt-2 text-xs text-[#00E5FF] hover:underline" data-testid={`call-transcript-toggle-${c.id}`}>
                    {open === c.id ? "Hide transcript" : `View transcript (${(c.transcript || []).length})`}
                  </button>
                  {open === c.id && (
                    <div className="mt-2 space-y-1 rounded-lg border border-white/8 bg-black/30 p-3">
                      {(c.transcript || []).length === 0 ? <div className="text-xs text-slate-500">No transcript captured.</div> :
                        c.transcript.map((t, i) => (
                          t.role === "system" ? (
                            <div key={i} className="my-1.5 flex items-center gap-2 text-[11px] font-medium text-[#c4b5fd]" data-testid={`transfer-note-${c.id}-${i}`}>
                              <span className="h-px flex-1 bg-[#8A2BE2]/30" />
                              <PhoneForwarded size={11} className="shrink-0" />
                              <span className="shrink-0">{t.text}</span>
                              <span className="h-px flex-1 bg-[#8A2BE2]/30" />
                            </div>
                          ) : (
                            <div key={i} className="text-xs">
                              <span className={["caller", "contact"].includes(t.role) ? "font-medium text-slate-400" : "font-medium text-[#00E5FF]"}>{t.role}:</span>{" "}
                              <span className="text-slate-200">{t.text}</span>
                            </div>
                          )
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );})}
        </div>
      ) : tab === "sms" ? (
        <div className="space-y-2" data-testid="calllog-sms">
          {data.sms.length === 0 ? (
            <div className="rounded-xl border border-white/8 bg-[#030A16]/40 p-6 text-center text-sm text-slate-500">No texts yet. When someone texts your number, the conversation shows here.</div>
          ) : data.sms.map((s) => (
            <div key={s.contact} className="rounded-xl border border-white/8 bg-[#030A16]/40 p-4" data-testid={`sms-row-${s.contact}`}>
              <div className="mb-2 flex items-center gap-2">
                <MessageSquare size={15} className="text-[#00E5FF]" />
                <span className="text-sm font-medium text-white">{s.name || s.contact}</span>
                {s.name && <span className="text-[11px] font-mono text-slate-500">{s.contact}</span>}
                <span className="text-[11px] text-slate-500">{fmt(s.updated_at)}</span>
              </div>
              <div className="space-y-1.5">
                {(s.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.role === "Nora" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-1.5 text-xs ${m.role === "Nora" ? "bg-[#00E5FF]/15 text-white" : "bg-white/8 text-slate-200"}`}>{m.text}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2" data-testid="calllog-scheduled">
          {scheduled.length === 0 ? (
            <div className="rounded-xl border border-white/8 bg-[#030A16]/40 p-6 text-center text-sm text-slate-500">No scheduled texts. Ask an associate to "text [name] a reminder tomorrow morning", or schedule one from a contact.</div>
          ) : scheduled.map((s) => {
            const badge = {
              pending: { label: "Scheduled", cls: "bg-[#FFBF00]/15 text-[#FFBF00]", Icon: Clock },
              sent: { label: "Sent", cls: "bg-[#22c55e]/15 text-[#4ade80]", Icon: CheckCircle2 },
              failed: { label: "Failed", cls: "bg-[#FF0055]/15 text-[#FF3B5C]", Icon: AlertCircle },
              canceled: { label: "Canceled", cls: "bg-white/8 text-slate-400", Icon: X },
              sending: { label: "Sending", cls: "bg-[#00E5FF]/15 text-[#00E5FF]", Icon: Loader2 },
            }[s.status] || { label: s.status, cls: "bg-white/8 text-slate-400", Icon: Clock };
            return (
              <div key={s.id} className="rounded-xl border border-white/8 bg-[#030A16]/40 p-4" data-testid={`scheduled-row-${s.id}`}>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 grid h-9 w-9 place-items-center rounded-lg bg-white/5 text-[#00E5FF]"><Clock size={16} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-white">{s.contact_name || s.phone}</span>
                      {s.contact_name && <span className="text-[11px] font-mono text-slate-500">{s.phone}</span>}
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase ${badge.cls}`}>
                        <badge.Icon size={10} /> {badge.label}
                      </span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-400">{s.when_local || fmt(s.send_at)}</div>
                    <div className="mt-1.5 rounded-lg border border-white/8 bg-black/30 px-3 py-1.5 text-xs text-slate-200">{s.message}</div>
                    {s.error && <div className="mt-1 text-[11px] text-[#FF3B5C]">{s.error}</div>}
                  </div>
                  {s.status === "pending" && (
                    <button onClick={() => cancelScheduled(s.id)} className="shrink-0 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-[#FF0055]/50 hover:text-[#FF3B5C]" data-testid={`scheduled-cancel-${s.id}`}>Cancel</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
